import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddProductModal from '../components/AddProductModal';
import ProductListHeader from '../components/product/list/ProductListHeader';
import ProductSearchFilters from '../components/product/list/ProductSearchFilters';
import ProductTable from '../components/product/list/ProductTable';
import ProductMobileCard from '../components/product/list/ProductMobileCard';
import useProducts from '../hooks/useProducts';
import useProductSelection from '../hooks/useProductSelection';
import Snackbar from '../components/Snackbar';

const ProductList = () => {
  const navigate = useNavigate();
  const { products, loading, error, pagination, fetchProducts, addProduct, deleteProducts, snackbar, closeSnackbar } = useProducts();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState('');
  
  // 필터 상태
  const [filters, setFilters] = useState({
    sourceSite: '',
    status: '',
    published_to_shopify: '',
    sortBy: 'crawled_at',
    sortOrder: 'desc'
  });
  
  // 체크박스 선택 관리
  const {
    selectedProducts,
    handleSelectAll,
    handleSelectProduct,
    clearSelection,
    isAllSelected,
    isIndeterminate
  } = useProductSelection(products);

  // 필터 관련 함수들
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSortChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSearch = () => {
    clearSelection();
    fetchProducts(1, filters);
  };

  // 페이징 관련 함수
  const handlePageChange = (page) => {
    clearSelection();
    fetchProducts(page, filters);
  };

  // 모달 관련 함수들
  const openModal = (site) => {
    setSelectedSite(site || '');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSite('');
  };

  const handleBulkAdd = async (category) => {
    try {
      closeModal();
      if (category) {
        await fetchProducts(1, filters);
      }
    } catch (error) {
      console.error('일괄 추가 중 오류 발생:', error);
      alert('일괄 추가 중 오류가 발생했습니다.');
    }
  };

  const handleSingleAdd = async (url, site) => {
    try {
      const siteToUse = site || selectedSite;
      await addProduct(siteToUse, url);
    } finally {
      closeModal();
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedProducts.size === 0) {
      alert('삭제할 제품을 선택해주세요.');
      return;
    }

    const confirmMessage = `선택된 ${selectedProducts.size}개 제품을 삭제하시겠습니까?`;
    if (window.confirm(confirmMessage)) {
      try {
        await deleteProducts(selectedProducts);
        // 삭제 성공 후 선택된 항목들 초기화
        clearSelection();
      } catch (error) {
        console.error('삭제 중 오류 발생:', error);
      }
    }
  };

  const handleDeleteProduct = async (productId) => {
    const confirmMessage = '정말로 이 제품을 삭제하시겠습니까?';
    if (window.confirm(confirmMessage)) {
      try {
        await deleteProducts(new Set([productId]));
      } catch (error) {
        console.error('삭제 중 오류 발생:', error);
      }
    }
  };

  const handleProductClick = (productId) => {
    navigate(`/products/${productId}`);
  };



  if (loading) {
    return (
      <div className="bg-white rounded-lg p-8 shadow-md w-full max-w-none mx-auto">
        {/* <h2 className="text-xl font-semibold text-gray-800">제품 관리</h2> */}
        <div className="text-center py-10 text-gray-600">
          제품 데이터를 불러오는 중...
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-8 shadow-md w-full max-w-none mx-auto">
      <ProductListHeader
        selectedCount={selectedProducts.size}
        onOpenModal={openModal}
        onDeleteSelected={handleDeleteSelected}
      />

      {/* 검색 필터 */}
      <ProductSearchFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onSortChange={handleSortChange}
        onSearch={handleSearch}
      />

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-5">
          {error}
        </div>
      )}
      
      {/* 데스크톱 테이블 */}
      <ProductTable
        products={products}
        pagination={pagination}
        onPageChange={handlePageChange}
        onProductClick={handleProductClick}
        onDeleteProduct={handleDeleteProduct}
        selectionHandlers={{
          handleSelectAll,
          handleSelectProduct,
          isAllSelected,
          isIndeterminate,
          selectedProducts
        }}
      />

      {/* 모바일용 카드 뷰 */}
      <div className="md:hidden mt-5">
        {!products || products.length === 0 ? (
          <div className="text-center py-10 text-gray-500">
            제품 데이터가 없습니다.
          </div>
        ) : (
          products.map(product => (
            <ProductMobileCard
              key={product.id}
              product={product}
              isSelected={selectedProducts.has(product.id)}
              onSelect={handleSelectProduct}
              onClick={() => handleProductClick(product.id)}
              onDeleteProduct={handleDeleteProduct}
            />
          ))
        )}
      </div>

      {/* 모달 */}
      <AddProductModal
        isOpen={isModalOpen}
        selectedSite={selectedSite}
        onClose={closeModal}
        onBulkAdd={handleBulkAdd}
        onSingleAdd={handleSingleAdd}
      />
      
      {/* 스낵바 */}
      <Snackbar
        isOpen={snackbar.isOpen}
        message={snackbar.message}
        type={snackbar.type}
        onClose={closeSnackbar}
      />
    </div>
  );
};

export default ProductList;